import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, unref as _unref, vModelText as _vModelText, withModifiers as _withModifiers, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, vShow as _vShow, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "default-trigger" }
const _hoisted_2 = { key: 0 }
const _hoisted_3 = { key: 1 }
const _hoisted_4 = {
  key: 0,
  class: "search-input"
}
const _hoisted_5 = { class: "options-list" }
const _hoisted_6 = ["onClick"]
const _hoisted_7 = { class: "option-label" }

import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import {
  useFloating,
  offset,
  flip,
  shift,
  autoUpdate,
  Placement,
} from "@floating-ui/vue";
import BaseCheckbox from "./BaseCheckbox.vue";

export interface SelectOption {
  label: string;
  value: string | number;
  disabled?: boolean;
}

interface Props {
  modelValue: string | number | (string | number)[];
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  multiple?: boolean;
  placement?: Placement;
  searchable?: boolean;
  maxHeight?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'BaseSelect',
  props: {
    modelValue: {},
    options: {},
    placeholder: { default: "Выберите значение" },
    disabled: { type: Boolean, default: false },
    multiple: { type: Boolean, default: false },
    placement: { default: "bottom-start" },
    searchable: { type: Boolean, default: false },
    maxHeight: { default: "300px" }
  },
  emits: ["update:model-value", "change"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const isOpen = ref(false);
const searchQuery = ref("");
const reference = ref<HTMLElement | null>(null);
const floating = ref<HTMLElement | null>(null);

const { x, y, strategy, update } = useFloating(reference, floating, {
  placement: props.placement,
  middleware: [offset(8), flip(), shift()],
  whileElementsMounted: autoUpdate,
});

const selectedOptions = computed(() => {
  if (props.multiple) {
    const selected = props.options.filter((option) =>
      (props.modelValue as (string | number)[]).includes(option.value)
    );
    return {
      items: selected,
      isMultiple: true,
      length: selected.length,
    };
  }
  const selected = props.options.find(
    (option) => option.value === props.modelValue
  );
  return {
    items: selected,
    isMultiple: false,
    label: selected?.label,
  };
});

const filteredOptions = computed(() => {
  if (!props.searchable || !searchQuery.value) {
    return props.options;
  }

  return props.options.filter((option) =>
    option.label.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
});

const isSelected = (option: SelectOption) => {
  if (props.multiple) {
    return (props.modelValue as (string | number)[]).includes(option.value);
  }
  return props.modelValue === option.value;
};

const handleSelect = (option: SelectOption) => {
  if (option.disabled) return;

  if (props.multiple) {
    const currentValue = [...(props.modelValue as (string | number)[])];
    const index = currentValue.indexOf(option.value);

    if (index === -1) {
      currentValue.push(option.value);
    } else {
      currentValue.splice(index, 1);
    }

    emit("update:model-value", currentValue);
    emit("change", currentValue);
  } else {
    emit("update:model-value", option.value);
    emit("change", option.value);
    isOpen.value = false;
  }
};

const handleClickOutside = (event: MouseEvent) => {
  if (
    reference.value &&
    !reference.value.contains(event.target as Node) &&
    floating.value &&
    !floating.value.contains(event.target as Node)
  ) {
    isOpen.value = false;
  }
};

const toggleDropdown = () => {
  if (!props.disabled) {
    isOpen.value = !isOpen.value;
    if (isOpen.value) {
      searchQuery.value = "";
    }
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});

watch(isOpen, (newValue) => {
  if (newValue) {
    update();
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["base-select", { disabled: _ctx.disabled }])
  }, [
    _createElementVNode("div", {
      ref_key: "reference",
      ref: reference,
      class: "select-trigger",
      onClick: toggleDropdown
    }, [
      _renderSlot(_ctx.$slots, "trigger", {
        selected: selectedOptions.value,
        isOpen: isOpen.value
      }, () => [
        _createElementVNode("div", _hoisted_1, [
          (_ctx.multiple)
            ? (_openBlock(), _createElementBlock("span", _hoisted_2, _toDisplayString(selectedOptions.value.length
                ? `${selectedOptions.value.length} выбрано`
                : _ctx.placeholder), 1))
            : (_openBlock(), _createElementBlock("span", _hoisted_3, _toDisplayString(selectedOptions.value.label || _ctx.placeholder), 1)),
          _createElementVNode("span", {
            class: _normalizeClass(["arrow", { 'is-open': isOpen.value }])
          }, "▼", 2)
        ])
      ])
    ], 512),
    _withDirectives(_createElementVNode("div", {
      ref_key: "floating",
      ref: floating,
      class: "select-dropdown",
      style: _normalizeStyle({
        position: _unref(strategy),
        top: _unref(y) ? `${_unref(y)}px` : '',
        left: _unref(x) ? `${_unref(x)}px` : '',
        maxHeight: _ctx.maxHeight,
      })
    }, [
      (_ctx.searchable)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
              type: "text",
              placeholder: "Поиск...",
              onClick: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["stop"]))
            }, null, 512), [
              [_vModelText, searchQuery.value]
            ])
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_5, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredOptions.value, (option) => {
          return (_openBlock(), _createElementBlock("div", {
            key: option.value,
            class: _normalizeClass(["option-item", { disabled: option.disabled }]),
            onClick: ($event: any) => (handleSelect(option))
          }, [
            (_ctx.multiple)
              ? (_openBlock(), _createBlock(BaseCheckbox, {
                  key: 0,
                  "model-value": isSelected(option),
                  disabled: option.disabled
                }, null, 8, ["model-value", "disabled"]))
              : _createCommentVNode("", true),
            _createElementVNode("span", _hoisted_7, _toDisplayString(option.label), 1)
          ], 10, _hoisted_6))
        }), 128))
      ])
    ], 4), [
      [_vShow, isOpen.value]
    ])
  ], 2))
}
}

})